import { useContext, useEffect, useState } from "react";
import { RegistrarIndicacao } from "./IndicacoesView";
import LiftCrmClientAction, {LiftCrmClientContext} from "./LiftCrmClientActions";
import {notifyProspRefresh} from "./newUi/prospRefresh";
import BindingDeadlineNotice from "./BindingDeadlineNotice";
import LiftCardPendingQueue from "./LiftCardPendingQueue";

const money = value => new Intl.NumberFormat("pt-BR", { style:"currency", currency:"BRL" }).format(Number(value || 0));
const date = value => {
  if (!value) return "—";
  const day = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (day) return `${day[3]}/${day[2]}/${day[1]}`;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleDateString("pt-BR", { timeZone:"America/Sao_Paulo" });
};
const ORIGINS = [
  ["lead_anuncio", "Lead do anúncio (Meta/Instagram)"],
  ["anuncio_loja", "Viu o anúncio e foi direto à loja"],
  ["porta", "Cliente de porta (presencial)"],
  ["prospeccao", "Prospecção"],
  ["indicacao", "Indicação"],
];

export function liftSaleIsLate(sale, now = Date.now()) {
  const created = Date.parse(sale?.pendencia?.criadaEm || "");
  return sale?.pendencia?.status === "aberta" && Number.isFinite(created) && now - created > 86400000;
}

async function jsonRequest(url, options) {
  const response = await fetch(url, { credentials:"same-origin", cache:"no-store", ...options });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Não foi possível concluir (HTTP ${response.status})`);
  return body;
}

function LiftSaleCard({ sale, onSaved, onIndicationSaved, manager, currentUser, allowExplicitSaleLink, users = [] }) {
  const crm=useContext(LiftCrmClientContext);
  const [action, setAction] = useState("");
  const [origin, setOrigin] = useState("");
  const [leadKey, setLeadKey] = useState("");
  const [vincularVenda, setVincularVenda] = useState(false);
  const [indicadoPor, setIndicadoPor] = useState("");
  const [leadQuery, setLeadQuery] = useState("");
  const [leads, setLeads] = useState([]);
  const [leadError, setLeadError] = useState("");
  const [observation, setObservation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [successNotice, setSuccessNotice] = useState("");
  const base = `/api/lift/pendencias/${encodeURIComponent(sale.storeId)}/${encodeURIComponent(sale.liftPedidoId)}`;
  const sellerId = Number(sale.vendidoPor?.consultantId || sale.responsavelId);
  const seller = users.find(user => Number(user.id) === sellerId);
  const sellerHomeStoreId = seller?.storeId
    || (Number(currentUser?.id) === sellerId ? currentUser?.storeId : null);
  const referralStoreId = currentUser?.role === "consultant"
    ? (sellerHomeStoreId || currentUser.storeId || sale.storeId)
    : (sellerHomeStoreId && Number(sellerHomeStoreId) !== Number(sale.storeId)
      ? sellerHomeStoreId : sale.storeId);
  const open = sale.pendencia?.status === "aberta";
  const late = liftSaleIsLate(sale);

  useEffect(() => {
    setVincularVenda(false);
    setSuccessNotice("");
    setError("");
  }, [sale.storeId, sale.liftPedidoId]);

  useEffect(()=>{
    if(sale.manualCardBinding?.status!=="confirmed")return;
    setError("");setLeadError("");setAction("");
    setSuccessNotice(`Venda #${sale.liftPedidoId} vinculada`);
  },[sale.manualCardBinding?.status,sale.liftPedidoId]);

  useEffect(() => {
    if (!allowExplicitSaleLink) setVincularVenda(false);
  }, [allowExplicitSaleLink]);

  useEffect(() => {
    if (action !== "origem" || origin !== "lead_anuncio") return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ storeId:String(sale.storeId), liftPedidoId:String(sale.liftPedidoId), q:leadQuery });
      jsonRequest(`/api/lift/pendencias/leads?${params}`)
        .then(body => { if (!cancelled) { setLeads(body.leads || []); setLeadError(""); } })
        .catch(err => { if (!cancelled) { setLeads([]); setLeadError(err.message); } });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [action, origin, leadQuery, sale.storeId, sale.liftPedidoId]);

  const submit = async (kind, body) => {
    setBusy(true);
    setError("");
    try {
      const result = await jsonRequest(`${base}/${kind}`, {
        method:"POST", headers:{ "Content-Type":"application/json" }, body:JSON.stringify(body),
      });
      onSaved(result.sale);
      if(body.vincularVenda&&result.card){
        crm?.onCardLinked?.(result);
        crm?.onOriginLinked?.(result.sale);
        notifyProspRefresh();
      }
      setAction("");
      setObservation("");
      if (body.vincularVenda) {
        setSuccessNotice(result.explicitSaleLink?.followUpStopped
          ? "Venda original do LIFT vinculada ao lead selecionado e Follow-up encerrado."
          : "Vínculo da venda original do LIFT ao lead confirmado.");
      }
      setVincularVenda(false);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const button = (label, click, disabled = false) =>
    <button type="button" disabled={busy || disabled} onClick={click}
      style={{ padding:"8px 12px", borderRadius:8, border:"1px solid #CBD5E1", background:"#fff", color:"#183049", cursor:"pointer", fontWeight:700 }}>
      {label}
    </button>;
  const inputStyle = { width:"100%", boxSizing:"border-box", padding:"9px 10px", borderRadius:8, border:"1px solid #CBD5E1", marginTop:6 };

  return <article style={{ background:open?"#FFF7F7":"#F0FDF4", border:`1px solid ${open?"#FCA5A5":"#86EFAC"}`,
    borderLeft:`5px solid ${open?"#DC2626":"#16A34A"}`, borderRadius:12, padding:16 }}>
    <div style={{ display:"flex", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
      <strong>Pedido #{sale.liftPedidoId} · {sale.loja || `Loja ${sale.storeId}`}</strong>
      <strong style={{ color:open?"#B91C1C":"#15803D" }}>{open?"Aberta":"Resolvido"}{late?" · Mais de 24h":""}</strong>
    </div>
    <div style={{ marginTop:8, lineHeight:1.6 }}>
      {date(sale.dataVenda)} · {money(sale.valorTotal)} · {sale.cliente?.nome || "Cliente não informado"}
      <br />{(sale.itens || []).map(item => `${item.produto || item.produtoLift || "Produto"} (${item.quantidade || 1})`).join(", ") || "Produtos não informados"}
      <br />LIFT: {sale.statusLift || "—"} · Entrega: {sale.entrega?.tipo === "encomenda" ? "Encomenda" : "Pronta entrega"} · Previsão: {date(sale.entrega?.previsaoUltimoItem)}
      {sale.vendidoPor?.vendaCruzada && <div style={{ fontWeight:700 }}>⇄ Venda cruzada — vendido por {sale.vendidoPor.nome || "consultor"}</div>}
      {manager && <div>Responsável: {sale.vendidoPor?.consultantId === sale.responsavelId
        ? (sale.vendidoPor?.nome || `Consultor #${sale.responsavelId}`)
        : `Responsável #${sale.responsavelId || "não informado"}`}</div>}
      {sale.ajustes && sale.valorOriginal != null && Number(sale.valorOriginal) !== Number(sale.valorTotal)
        && <div>Valor original {money(sale.valorOriginal)} → atual {money(sale.valorTotal)}</div>}
      {manager && sale.sumiuDoLiftEm && <div role="alert" style={{ color:"#B91C1C", fontWeight:700 }}>⚠ Pedido não aparece mais no LIFT</div>}
    </div>
    <BindingDeadlineNotice sale={sale} />
    <LiftCrmClientAction key={sale.manualCardBinding?.status||"unconfirmed"}
      storeId={sale.storeId} pedido={sale.liftPedidoId} sale={sale} />
    <div style={{ marginTop:10, fontSize:13 }}>
      Origem: {ORIGINS.find(([key]) => key === sale.classificacaoOrigem?.tipo)?.[1] || (sale.pendencia?.precisaOrigem ? "Pendente" : "Não exigida")}
      {sale.classificacaoOrigem?.indicadoPor && ` · indicado por ${sale.classificacaoOrigem.indicadoPor}`}
      {" · "}Indicações: {Number(sale.indicacoesResumo?.quantidade || 0)}/3
      {sale.indicacoesResumo?.recusou && " · Cliente não quis indicar"}
      {!open && sale.pendencia?.precisaIndicacoes
        && !sale.indicacoesResumo?.recusou && Number(sale.indicacoesResumo?.quantidade || 0) < 3
        && <div style={{ color:"#64748B", marginTop:4 }}>Indicações pendentes — não impedem a resolução do vínculo.</div>}
    </div>
    <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginTop:12 }}>
      {sale.pendencia?.precisaOrigem && button("Registrar origem", () => {
        setAction("origem");
        setOrigin(sale.classificacaoOrigem?.tipo || "");
        setLeadKey("");
        setVincularVenda(false);
        setSuccessNotice("");
      })}
      {button("Registrar indicação", () => setAction("indicacoes"))}
      {button("Cliente não quis indicar", () => setAction("recusa"))}
    </div>
    {action === "origem" && <div style={{ marginTop:12 }}>
      <label>De onde veio esse cliente?
        <select style={inputStyle} value={origin} disabled={busy} onChange={e => {
          setOrigin(e.target.value);
          setLeadKey("");
          setVincularVenda(false);
          setSuccessNotice("");
        }}>
          <option value="">Selecione a origem</option>
          {ORIGINS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
      {origin === "lead_anuncio" && <>
        <input aria-label="Buscar lead por nome ou telefone" style={inputStyle} value={leadQuery} disabled={busy}
          onChange={e => setLeadQuery(e.target.value)} placeholder="Buscar lead da loja (últimos 60 dias)" />
        {leadError && <div role="alert">{leadError}</div>}
        <select aria-label="Lead do anúncio" style={inputStyle} value={leadKey} disabled={busy} onChange={e => {
          setLeadKey(e.target.value);
          setVincularVenda(false);
          setSuccessNotice("");
        }}>
          <option value="">Selecione um lead</option>
          {leads.map(lead => <option key={lead.chave} value={lead.chave}>{lead.nome} · {lead.telefone}</option>)}
        </select>
        {allowExplicitSaleLink && <>
          <label style={{ display:"flex", alignItems:"flex-start", gap:8, marginTop:10, fontSize:13, fontWeight:700 }}>
            <input type="checkbox" checked={vincularVenda} disabled={!leadKey || busy}
              onChange={e => setVincularVenda(e.target.checked)} />
            Vincular esta venda ao lead e encerrar o Follow-up
          </label>
          <p style={{ margin:"4px 0 0", fontSize:12, color:"#64748B" }}>
            Confirme que esta é a venda original do LIFT correspondente ao lead selecionado; isso não cria uma nova venda.
          </p>
        </>}
      </>}
      {origin === "indicacao" && <input aria-label="Quem indicou o cliente" style={inputStyle} value={indicadoPor}
        onChange={e => setIndicadoPor(e.target.value)} placeholder="Nome de quem indicou" />}
      <div style={{ marginTop:8 }}>{button("Salvar origem", () => submit("origem", {
        tipo:origin,
        leadChave:leadKey,
        indicadoPor,
        ...(allowExplicitSaleLink && vincularVenda && leadKey ? { vincularVenda:true } : {}),
      }),
        !origin || (origin === "lead_anuncio" && !leadKey) || (origin === "indicacao" && !indicadoPor.trim()))}</div>
    </div>}
      {action === "indicacoes" && <RegistrarIndicacao
        sourceSale={{ type:"lift", storeId:Number(sale.storeId), id:String(sale.liftPedidoId) }}
        referralStoreId={Number(referralStoreId)}
        defaultIndicadoPor={Number(referralStoreId) === Number(sale.storeId)
          ? (sale.cliente?.nome || sale.clienteNome || sale.nomeCliente || "") : ""}
        onSaved={onIndicationSaved}
      />}
    {action === "recusa" && <div style={{ marginTop:12 }}>
      <input aria-label="Observação da recusa" style={inputStyle} value={observation} onChange={e => setObservation(e.target.value)} placeholder="Observação (opcional)" />
      <div style={{ marginTop:8 }}>{button("Confirmar recusa", () => submit("recusa", { observacao:observation }))}</div>
    </div>}
    {error && <div role="alert" style={{ marginTop:8, color:"#B91C1C" }}>{error}</div>}
    {successNotice && <div role="status" style={{ marginTop:8, color:"#15803D", fontWeight:700 }}>{successNotice}</div>}
  </article>;
}

export default function LiftPendingSales({ sales = [], error, onSaved, onIndicationSaved, currentUser, authenticatedUser, users = [] }) {
  sales=sales.map(sale=>sale.manualCardBinding?.status==='confirmed'
    ? {...sale,pendencia:{...sale.pendencia,status:"resolvida",precisaOrigem:false}}
    : sale);
  const open = sales.filter(sale => sale.pendencia?.status === "aberta");
  const resolved = sales.filter(sale => sale.pendencia?.status === "resolvida");
  const manager = currentUser?.role !== "consultant";
  const operator = authenticatedUser === undefined ? currentUser : authenticatedUser;
  const allowExplicitSaleLink = currentUser?.role === "consultant"
    && operator?.role === "consultant"
    && Number(operator.id) === Number(currentUser?.id);
  return <section id="pendencias-venda" style={{ marginBottom:24 }}>
    <h2 style={{ fontSize:18, margin:"0 0 6px" }}>Pendências de venda · LIFT</h2>
    <p style={{ fontSize:13, color:"#64748B" }}>Abertas: {open.length} · Abertas há mais de 24h: {open.filter(s => liftSaleIsLate(s)).length} · Resolvidas dos últimos 30 dias: {resolved.length}</p>
    {error && <p role="alert" style={{ color:"#B91C1C" }}>{error}</p>}
    {!error && !sales.length && <p>Nenhuma pendência de venda no momento.</p>}
    <div style={{ display:"grid", gap:12 }}>
      {[...open.sort((a,b) => String(a.pendencia?.criadaEm || "").localeCompare(String(b.pendencia?.criadaEm || ""))),
        ...resolved.sort((a,b) => String(b.pendencia?.resolvidaEm || "").localeCompare(String(a.pendencia?.resolvidaEm || "")))]
          .map(sale => <LiftSaleCard key={`${sale.storeId}_${sale.liftPedidoId}`} sale={sale} onSaved={onSaved} onIndicationSaved={onIndicationSaved} manager={manager} currentUser={currentUser} allowExplicitSaleLink={allowExplicitSaleLink} users={users} />)}
    </div>
    {(currentUser?.role === "manager" || currentUser?.role === "admin" || currentUser?.role === "superadmin")
      && <LiftCardPendingQueue currentUser={currentUser} />}
  </section>;
}